import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "../Header";
import Footer from "../Footer";
import API_BASE_URL from "../../Config";
import { Link } from "react-router-dom";

/* ══════════════════════════════════════════════
   SVG ICON SYSTEM
══════════════════════════════════════════════ */
const Icon = ({ d, size = 18, stroke = 1.8, fill = "none" }) => (
  <svg
    width={size} height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon    = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></>}/>;
const PlusIcon      = (p) => <Icon {...p} d={<path d="M12 5v14M5 12h14"/>}/>;
const EditIcon      = (p) => <Icon {...p} d={<><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z"/></>}/>;
const TrashIcon     = (p) => <Icon {...p} d={<><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></>}/>;
const CloseIcon     = (p) => <Icon {...p} d={<path d="M18 6 6 18M6 6l12 12"/>}/>;
const ChevronDown   = (p) => <Icon {...p} d={<path d="m6 9 6 6 6-6"/>}/>;
const ChevronLeftIcon  = (p) => <Icon {...p} d={<path d="m15 18-6-6 6-6"/>}/>;
const ChevronRightIcon = (p) => <Icon {...p} d={<path d="m9 18 6-6-6-6"/>}/>;
const CheckIcon     = (p) => <Icon {...p} d={<path d="M20 6 9 17l-5-5"/>}/>;
const UploadIcon    = (p) => <Icon {...p} d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>}/>;
const PackageIcon   = (p) => <Icon {...p} d={<><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></>}/>;
const FilterIcon    = (p) => <Icon {...p} d={<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>}/>;
const ExcelIcon     = (p) => <Icon {...p} d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>}/>;
const TagIcon       = (p) => <Icon {...p} d={<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>}/>;
const GripIcon      = (p) => <Icon {...p} d={<><circle cx="9" cy="5" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="9" cy="19" r="1"/><circle cx="15" cy="19" r="1"/></>}/>;
const InfoIcon      = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>}/>;

/* ══════════════════════════════════════════════
   iOS TOGGLE SWITCH
══════════════════════════════════════════════ */
const IosSwitch = ({ checked, onChange, disabled = false, colorOn = "var(--pl-primary-grad)" }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`ios-switch ${checked ? "is-on" : "is-off"}`}
    style={{ "--switch-on-bg": colorOn }}
    title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
  >
    <span className="ios-switch__thumb" />
  </button>
);

/* ══════════════════════════════════════════════
   CONFIRM DIALOG
══════════════════════════════════════════════ */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   MULTI SELECT FILTER DROPDOWN
══════════════════════════════════════════════ */
const MultiSelectFilter = ({ options, selected, onChange, placeholder = "Filter…" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (val) =>
    onChange(selected.includes(val) ? selected.filter((s) => s !== val) : [...selected, val]);

  const label = selected.length === 0 ? placeholder
    : selected.length === 1 ? selected[0]
    : `${selected.length} selected`;

  return (
    <div className="pl-ms-wrap" ref={ref}>
      <button type="button" className={`pl-ms-trigger ${open ? "open" : ""}`} onClick={() => setOpen((o) => !o)}>
        <FilterIcon size={14} />
        <span>{label}</span>
        {selected.length > 0 && <span className="pl-ms-count">{selected.length}</span>}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="pl-ms-dropdown">
          {selected.length > 0 && (
            <button type="button" className="pl-ms-clearall" onClick={() => { onChange([]); setOpen(false); }}>
              Clear all
            </button>
          )}
          {options.map((opt) => (
            <label key={opt.value} className={`pl-ms-option ${selected.includes(opt.value) ? "checked" : ""}`}>
              <span className="pl-ms-checkbox">
                {selected.includes(opt.value) && <CheckIcon size={10} stroke={3} />}
              </span>
              {opt.dot && <span className="pl-ms-dot" style={{ background: opt.dot }} />}
              {opt.label}
              <input type="checkbox" hidden checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   CUSTOM PAGINATION
══════════════════════════════════════════════ */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  const start = totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows);
  const end   = Math.min(currentPage * perPage, totalRows);

  return (
    <div className="pl-pagination">
      <div className="pl-pagination__info">
        <span>Showing <b>{start}</b>–<b>{end}</b> of <b>{totalRows}</b></span>
        <select
          className="pl-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="pl-pagination__pages">
        <button
          className="pl-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={16} />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="pl-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`pl-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="pl-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   IMAGE UPLOAD & DRAG-REORDER
══════════════════════════════════════════════ */
const ImageUploader = ({ images, onChange }) => {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const addFiles = (files) => {
    const newImgs = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
    }));
    onChange([...images, ...newImgs]);
  };

  const remove = (id) => {
    onChange(images.filter((img) => img.id !== id));
  };

  const onDragStart = (idx) => { dragItem.current = idx; };
  const onDragEnter = (idx) => { dragOverItem.current = idx; };
  const onDragEnd = () => {
    const copy = [...images];
    const dragged = copy.splice(dragItem.current, 1)[0];
    copy.splice(dragOverItem.current, 0, dragged);
    dragItem.current = null;
    dragOverItem.current = null;
    onChange(copy);
  };

  return (
    <div className="pl-img-uploader">
      <div
        className={`pl-img-dropzone ${dragOver ? "over" : ""}`}
        onClick={() => fileRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
      >
        <UploadIcon size={28} />
        <p>Drop images here or <span>browse</span></p>
        <small>Supports PNG, JPG, WEBP · Multiple allowed</small>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => addFiles(e.target.files)} />
      </div>
      {images.length > 0 && (
        <div className="pl-img-grid">
          {images.map((img, idx) => (
            <div
              key={img.id}
              className="pl-img-thumb"
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragEnter={() => onDragEnter(idx)}
              onDragEnd={onDragEnd}
            >
              {idx === 0 && <span className="pl-img-primary-tag">Primary</span>}
              <img src={img.preview || `${API_BASE_URL}public/productimages/${img.file}`} alt="" />
              <div className="pl-img-overlay">
                <span className="pl-img-grip"><GripIcon size={14} /></span>
                <button type="button" className="pl-img-remove" onClick={() => remove(img.id)}>
                  <CloseIcon size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {images.length > 0 && (
        <p className="pl-img-hint">
          <GripIcon size={13} /> Drag images to reorder. First image is the primary product image.
        </p>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   EXCEL IMPORT MODAL
══════════════════════════════════════════════ */
const ExcelImportModal = ({ onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem("authToken");

  const handleImport = () => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    fetch(`${API_BASE_URL}api/products/import`, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status) { onImport(); onClose(); }
        else alert(data.message || "Import failed.");
      })
      .catch(() => alert("Import failed. Please try again."))
      .finally(() => setUploading(false));
  };

  return (
    <div className="pl-modal-overlay" onClick={onClose}>
      <div className="pl-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="pl-modal-head">
          <div className="pl-modal-title">
            <span className="pl-modal-icon" style={{ background: "linear-gradient(135deg,#22c55e,#16a34a)" }}>
              <ExcelIcon size={16} />
            </span>
            Import Products via Excel
          </div>
          <button className="pl-modal-close" onClick={onClose}><CloseIcon size={17} /></button>
        </div>
        <div className="pl-modal-body">
          <p className="pl-import-desc">
            Upload an <b>.xlsx</b> or <b>.csv</b> file to bulk-import products. The file must include columns:
            <code>name, category, price, stock</code>.
          </p>
          <div
            className={`pl-import-drop ${file ? "has-file" : ""}`}
            onClick={() => fileRef.current.click()}
          >
            <ExcelIcon size={32} />
            {file ? (
              <><p className="pl-import-filename">{file.name}</p><small>{(file.size / 1024).toFixed(1)} KB</small></>
            ) : (
              <><p>Click or drop your Excel / CSV file here</p><small>Max file size: 10 MB</small></>
            )}
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => setFile(e.target.files[0])} />
          </div>
          <a
            href={`${API_BASE_URL}public/templates/product_import_template.xlsx`}
            className="pl-template-link"
            download
          >
            ↓ Download sample template
          </a>
        </div>
        <div className="pl-modal-foot">
          <button className="pl-btn-light" onClick={onClose}>Cancel</button>
          <button className="pl-btn-primary" onClick={handleImport} disabled={!file || uploading}>
            {uploading ? "Importing…" : "Import Now"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   PRODUCT LIST (MAIN COMPONENT)
══════════════════════════════════════════════ */
const ProductList = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [stockFilter, setStockFilter] = useState([]);
  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [switchingStatusId, setSwitchingStatusId] = useState(null);
  const [switchingStockId, setSwitchingStockId] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
const debounceRef = useRef(null);


useEffect(() => {
  clearTimeout(debounceRef.current);
  debounceRef.current = setTimeout(() => {
    setDebouncedSearch(filterText);
    setPage(1); // reset to page 1 on new search
  }, 400);
  return () => clearTimeout(debounceRef.current);
}, [filterText]);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", confirmLabel: "Confirm",
    confirmVariant: "danger", onConfirm: null,
  });

  const token = localStorage.getItem("authToken");

  /* ── Confirm Dialog helpers ── */
  const showConfirm = ({ title, message, confirmLabel, confirmVariant = "danger", onConfirm }) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmVariant, onConfirm });
  };
  const closeConfirm = () => setConfirmDialog((p) => ({ ...p, open: false, onConfirm: null }));

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg); setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
  };

  /* ── Fetch ── */
 const fetchProducts = useCallback((p = 1, size = perPage, search = debouncedSearch) => {
  setLoading(true);
  fetch(`${API_BASE_URL}api/products?page=${p}&per_page=${size}&search=${encodeURIComponent(search)}`, {
    method: "GET",
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
  })
    .then((r) => r.json())
    .then((data) => {
      if (data.status && Array.isArray(data.data)) {
        setProducts(data.data);
        setFilteredProducts(data.data); // no client filtering needed
        setTotalRows(data.pagination?.total || data.data.length);
        setPage(data.pagination?.current_page || p);
      }
    })
    .catch((err) => console.error("Error fetching products:", err))
    .finally(() => setLoading(false));
}, [token, debouncedSearch, perPage]);

useEffect(() => {
  fetchProducts(1, perPage, debouncedSearch);
}, [debouncedSearch]);

  // useEffect(() => { fetchProducts(1, perPage); }, []);

  /* ── Pagination handlers ── */
  const handlePageChange = (p) => {
    setPage(p);
    fetchProducts(p, perPage, debouncedSearch);
  };
  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
     fetchProducts(1, newPerPage, debouncedSearch);
  };
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  /* ── Combined Filter ── */
 

  /* ── Status Toggle ── */
  const handleStatusChange = (id, currentStatus) => {
    if (switchingStatusId === id) return;
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    showConfirm({
      title: "Change Status",
      message: `Are you sure you want to set this product to "${newStatus}"?`,
      confirmLabel: "Yes, Change",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setSwitchingStatusId(id);
        fetch(`${API_BASE_URL}api/products/status/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.status) { flashMessage(`Status updated to ${newStatus}`); fetchProducts(page, perPage); }
            else flashMessage("Failed to update status.", "danger");
          })
          .catch(() => flashMessage("Error updating status.", "danger"))
          .finally(() => setSwitchingStatusId(null));
      },
    });
  };

  /* ── Stock Status Toggle ── */
  const handleStockStatusChange = (id, currentStockStatus) => {
    if (switchingStockId === id) return;
    const newStockStatus = currentStockStatus === "In Stock" ? "Out Of Stock" : "In Stock";
    showConfirm({
      title: "Change Stock Status",
      message: `Are you sure you want to set this product to "${newStockStatus}"?`,
      confirmLabel: "Yes, Change",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setSwitchingStockId(id);
        fetch(`${API_BASE_URL}api/products/stock/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.status) { flashMessage(`Stock status updated to ${newStockStatus}`); fetchProducts(page, perPage); }
            else flashMessage("Failed to update stock status.", "danger");
          })
          .catch(() => flashMessage("Error updating stock status.", "danger"))
          .finally(() => setSwitchingStockId(null));
      },
    });
  };

  /* ── Delete Single ── */
  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Product",
      message: "Are you sure you want to delete this product? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/products/destroy/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.status) { flashMessage("Product deleted successfully!"); fetchProducts(page, perPage); }
            else flashMessage("Failed to delete product.", "danger");
          })
          .catch(() => flashMessage("Error deleting product.", "danger"));
      },
    });
  };

  /* ── Bulk Delete ── */
  const handleBulkDelete = () => {
    if (!selectedRows.length) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected product${count > 1 ? "s" : ""}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: async () => {
        closeConfirm();
        setBulkDeleting(true);
        try {
          const results = await Promise.all(
            selectedRows.map((row) =>
              fetch(`${API_BASE_URL}api/products/destroy/${row.id}`, {
                method: "GET",
                headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
              }).then((r) => r.json()).catch(() => ({ status: false }))
            )
          );
          const successCount = results.filter((r) => r.status).length;
          const failCount = count - successCount;
          if (failCount === 0) {
            flashMessage(`${successCount} product${successCount > 1 ? "s" : ""} deleted successfully!`);
          } else {
            flashMessage(`${successCount} deleted, ${failCount} failed.`, successCount > 0 ? "warning" : "danger");
          }
          setSelectedRows([]);
          setToggleCleared((t) => !t);
          fetchProducts(page, perPage);
        } catch {
          flashMessage("Error during bulk delete.", "danger");
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };

  /* ── Image helper ── */
  const getFirstImage = (row) => {
    if (Array.isArray(row.images) && row.images.length > 0) return row.images[0];
    if (typeof row.images === "string" && row.images.includes(",")) return row.images.split(",")[0];
    if (typeof row.images === "string" && row.images) return row.images;
    return null;
  };

  /* ── Table Columns ── */
  const columns = useMemo(() => [
    {
      name: "ID",
      selector: (row) => row.id,
      cell: (row) => <span className="pl-id-pill">#{row.id}</span>,
      width: "80px",
      sortable: true,
    },
    {
      name: "Image",
      cell: (row) => {
        const img = getFirstImage(row);
        return (
          <div className="pl-img-cell">
            <img
              src={img ? `${API_BASE_URL}public/productimages/${img}` : `${API_BASE_URL}public/noimage.jpg`}
              alt={row.name}
            />
          </div>
        );
      },
      width: "90px",
    },
    {
      name: "Product",
      selector: (row) => row.name,
      cell: (row) => (
        <div className="pl-product-cell">
          <span className="pl-product-name">{row.name}</span>
          {row.brand && <span className="pl-product-brand">{row.brand}</span>}
        </div>
      ),
      sortable: true,
      grow: 2,
      minWidth: "180px",
    },
    {
      name: "Category",
      selector: (row) => row.category_title?.name || row.category || "—",
      cell: (row) => (
        <span className="pl-tag-pill">{row.category_title?.name || row.category || "—"}</span>
      ),
      sortable: true,
      minWidth: "130px",
    },
    {
      name: "Sub Category",
      selector: (row) => row.sub_category_title?.name || row.subcategory || "—",
      cell: (row) => (
        <span className="pl-tag-pill secondary">{row.sub_category_title?.name || row.subcategory || "—"}</span>
      ),
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Price",
      selector: (row) => parseFloat(row.price || 0),
      cell: (row) => (
        <span className="pl-price-cell">₹{parseFloat(row.price || 0).toFixed(2)}</span>
      ),
      sortable: true,
      right: true,
      width: "110px",
    },
    {
      name: "Stock Qty",
      selector: (row) => row.stock_available || 0,
      cell: (row) => <span className="pl-stock-qty">{row.stock_available || 0}</span>,
      sortable: true,
      right: true,
      width: "100px",
    },
    {
      name: "Status",
      cell: (row) => (
        <div className="pl-toggle-wrap">
          <IosSwitch
            checked={row.status === "Active"}
            onChange={() => handleStatusChange(row.id, row.status)}
            disabled={switchingStatusId === row.id}
          />
          <span className={`pl-toggle-label ${row.status === "Active" ? "is-active" : "is-inactive"}`}>
            {row.status === "Active" ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      width: "150px",
    },
    {
      name: "Stock",
      cell: (row) => (
        <div className="pl-toggle-wrap">
          <IosSwitch
            checked={row.stock_available === "In Stock"}
            onChange={() => handleStockStatusChange(row.id, row.stock_available)}
            disabled={switchingStockId === row.id}
            colorOn="linear-gradient(135deg, #3b82f6, #2563eb)"
          />
          <span className={`pl-toggle-label ${row.stock_available === "In Stock" ? "in-stock" : "out-stock"}`}>
            {row.stock_available === "In Stock" ? "In Stock" : "Out"}
          </span>
        </div>
      ),
      width: "140px",
    },
    {
      name: "Created",
      selector: (row) => row.created_at,
      cell: (row) => (
        <span className="pl-date-cell">
          {row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
        </span>
      ),
      sortable: true,
      minWidth: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="pl-row-actions">
          <Link to={`/product/edit/${row.id}`} className="pl-ico-btn edit" title="Edit Product">
            <EditIcon size={15} />
          </Link>
          <button className="pl-ico-btn del" onClick={() => handleDelete(row.id)} title="Delete Product">
            <TrashIcon size={15} />
          </button>
        </div>
      ),
      width: "110px",
      right: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [switchingStatusId, switchingStockId]);

  /* ── Table Styles ── */
  const tableStyles = useMemo(() => ({
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "linear-gradient(135deg, #f0fbf6 0%, #e8f8f0 100%)",
        borderTopLeftRadius: "0",
        borderTopRightRadius: "0",
        borderBottom: "1px solid #d6efe2",
        minHeight: "50px",
      },
    },
    headCells: {
      style: {
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#4f6b5e",
        paddingLeft: "14px",
        paddingRight: "14px",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        fontSize: "13.5px",
        color: "#1a2e27",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "14px", paddingRight: "14px" } },
    pagination: { style: { display: "none" } },
  }), []);

  const statusOptions = [
    { value: "Active", label: "Active", dot: "#10b981" },
    { value: "InActive", label: "Inactive", dot: "#e0413c" },
  ];
  const stockOptions = [
    { value: "In Stock", label: "In Stock", dot: "#10b981" },
    { value: "Out Of Stock", label: "Out of Stock", dot: "#f59e0b" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=inter:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap');

        :root {
          --pl-bg:           #f3faf6;
          --pl-card:         #ffffff;
          --pl-border:       #e3f0e9;
          --pl-text:         #0f1f1a;
          --pl-muted:        #6b7a74;
          --pl-primary:      #10b981;
          --pl-primary-d:    #059669;
          --pl-primary-grad: #2F4A3A;
          --pl-soft:         #ecfdf5;
          --pl-edit-bg:      #fff7ed;
          --pl-edit-fg:      #ea7a3b;
          --pl-del-bg:       #fee4e2;
          --pl-del-fg:       #e0413c;
          --pl-shadow:       0 1px 2px rgba(16,30,24,.04), 0 4px 16px rgba(16,30,24,.06);
          --pl-shadow-lg:    0 8px 40px rgba(16,30,24,.14);
        }

        /* ── Shell ── */
        .pl-page { font-family: 'Inter', sans-serif; background: var(--pl-bg); min-height: 100vh; }
        .pl-page .page-content {
          padding: calc(var(--bs-app-header-height, 70px) + 16px) 0.9rem 50px 16px !important;
        }

        /* ── Page Header ── */
        .pl-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; }
        .pl-head-left h2 {
          font-family: 'inter', sans-serif;
          font-size: clamp(22px, 2.4vw, 28px); font-weight: 800;
          color: var(--pl-text); margin: 0 0 4px; letter-spacing: -0.5px;
        }
        .pl-head-left p  { color: var(--pl-muted); font-size: 13.5px; margin: 0; }
        .pl-head-actions { display: flex; gap: 10px; flex-wrap: wrap; }

        /* ── Buttons ── */
        .pl-btn-primary {
          display: inline-flex; align-items: center; gap: 7px;
          background: var(--pl-primary-grad); color: #fff; border: none;
          padding: 11px 20px; font-weight: 700; font-size: 13.5px;
          border-radius: 12px; cursor: pointer;
          box-shadow: 0 6px 18px rgba(16,185,129,.28);
          transition: transform .15s, box-shadow .2s, filter .2s;
          white-space: nowrap; font-family: 'inter', sans-serif; text-decoration: none;
          letter-spacing: 0.01em;
        }
        .pl-btn-primary svg { fill: none !important; }
        .pl-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 26px rgba(16,185,129,.36); filter: brightness(1.06); color: #fff; }
        .pl-btn-primary:active { transform: translateY(0); }
        .pl-btn-primary:disabled { opacity: .6; cursor: not-allowed; transform: none; }

        .pl-btn-secondary {
          display: inline-flex; align-items: center; gap: 7px;
          background: #fff; color: var(--pl-text); border: 1.5px solid var(--pl-border);
          padding: 11px 18px; font-weight: 600; font-size: 13.5px;
          border-radius: 12px; cursor: pointer;
          box-shadow: 0 1px 4px rgba(16,30,24,.06);
          transition: all .15s; white-space: nowrap; font-family: 'Inter', sans-serif;
        }
        .pl-btn-secondary svg { fill: none !important; color: var(--pl-primary); }
        .pl-btn-secondary:hover { border-color: var(--pl-primary); background: var(--pl-soft); transform: translateY(-1px); }

        .pl-btn-light {
          display: inline-flex; align-items: center; gap: 6px;
          background: #fff; border: 1.5px solid var(--pl-border);
          padding: 10px 18px; border-radius: 11px; font-weight: 600;
          font-size: 14px; color: var(--pl-muted); cursor: pointer;
          transition: background .14s; font-family: 'Inter', sans-serif;
        }
        .pl-btn-light:hover { background: #f4faf7; }

        /* ── Card ── */
        .pl-card { background: var(--pl-card); border: 1px solid var(--pl-border); border-radius: 18px; box-shadow: var(--pl-shadow); overflow: hidden; }

        /* ── Card Top ── */
        .pl-card-top { display: flex; align-items: center; gap: 10px; padding: 16px 18px; flex-wrap: wrap; border-bottom: 1px solid #f0f7f4; }
        .pl-search-box { position: relative; flex: 1 1 240px; max-width: 360px; }
        .pl-search-box svg { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #9db5aa; fill: none !important; }
        .pl-search-box input {
          width: 100%; padding: 10px 14px 10px 40px;
          border-radius: 999px; border: 1px solid var(--pl-border);
          background: #f6fbf8; font-size: 13.5px; color: var(--pl-text);
          outline: none; transition: all .15s; font-family: 'Inter', sans-serif;
        }
        .pl-search-box input::placeholder { color: #9db5aa; }
        .pl-search-box input:focus { border-color: var(--pl-primary); background: #fff; box-shadow: 0 0 0 4px rgba(16,185,129,.12); }
        .pl-total-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; background: #fff; border: 1px solid var(--pl-border);
          border-radius: 999px; font-size: 13px; color: var(--pl-text); font-weight: 600;
          white-space: nowrap;
        }
        .pl-total-badge svg { fill: none !important; color: var(--pl-primary); }
        .pl-total-badge b { color: var(--pl-primary-d); }

        /* ── MultiSelect Filter ── */
        .pl-ms-wrap { position: relative; }
        .pl-ms-trigger {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 9px 13px; background: #fff; border: 1px solid var(--pl-border);
          border-radius: 999px; font-size: 13px; color: var(--pl-text);
          font-weight: 600; cursor: pointer; white-space: nowrap;
          transition: all .15s; font-family: 'Inter', sans-serif;
        }
        .pl-ms-trigger svg { fill: none !important; color: var(--pl-muted); }
        .pl-ms-trigger:hover, .pl-ms-trigger.open { border-color: var(--pl-primary); box-shadow: 0 0 0 3px rgba(16,185,129,.10); }
        .pl-ms-count { background: var(--pl-primary); color: #fff; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; }
        .pl-ms-dropdown {
          position: absolute; top: calc(100% + 8px); left: 0; min-width: 180px;
          background: #fff; border: 1px solid var(--pl-border); border-radius: 14px;
          box-shadow: var(--pl-shadow-lg); z-index: 300; padding: 8px;
          animation: plDropIn .18s ease;
        }
        @keyframes plDropIn { from { opacity:0; transform: translateY(-6px); } to { opacity:1; transform: translateY(0); } }
        .pl-ms-clearall {
          display: block; width: 100%; padding: 7px 10px;
          background: #fff7ed; color: #c05621; border: none; border-radius: 8px;
          font-size: 12px; font-weight: 700; cursor: pointer; text-align: left;
          margin-bottom: 6px; font-family: 'Inter', sans-serif; transition: background .14s;
        }
        .pl-ms-clearall:hover { background: #feecdc; }
        .pl-ms-option {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 10px; border-radius: 9px; cursor: pointer;
          font-size: 13px; font-weight: 500; color: var(--pl-text);
          transition: background .14s; user-select: none;
        }
        .pl-ms-option:hover { background: var(--pl-soft); }
        .pl-ms-option.checked { background: #f0fbf6; color: var(--pl-primary-d); font-weight: 600; }
        .pl-ms-checkbox {
          width: 17px; height: 17px; border: 1.5px solid #c7d3ce; border-radius: 5px;
          display: grid; place-items: center; flex-shrink: 0; transition: all .14s; background: #fff;
        }
        .pl-ms-option.checked .pl-ms-checkbox { background: var(--pl-primary); border-color: var(--pl-primary); }
        .pl-ms-checkbox svg { color: #fff; fill: none !important; }
        .pl-ms-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

        /* ── Alert ── */
        .pl-alert {
          margin: 14px 18px 0; padding: 12px 16px; border-radius: 12px;
          font-size: 13.5px; font-weight: 500;
          display: flex; align-items: center; gap: 10px;
          animation: plDropIn .25s ease;
        }
        .pl-alert.success { background: #ecfdf5; color: #065f46; border: 1px solid #b7e8d2; }
        .pl-alert.danger  { background: #fff1f0; color: #991b1b; border: 1px solid #fbd7d3; }
        .pl-alert.warning { background: #fefce8; color: #92400e; border: 1px solid #fde68a; }
        .pl-alert svg { fill: none !important; flex-shrink: 0; }
        .pl-alert .close {
          margin-left: auto; background: none; border: none; color: inherit;
          opacity: .6; cursor: pointer; font-size: 18px; line-height: 1;
        }
        .pl-alert .close:hover { opacity: 1; }

        /* ── Bulk Bar ── */
        .pl-bulk-bar {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          padding: 12px 22px;
          background: linear-gradient(135deg, rgba(20,184,166,.08), rgba(16,185,129,.05));
          border-bottom: 1px solid rgba(16,185,129,.15);
          font-size: 13.5px; animation: plDropIn .25s ease;
        }
        .pl-bulk-count { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; color: var(--pl-text); font-size: 13.5px; }
        .pl-badge-num { background: var(--pl-primary); color: #fff; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 700; }
        .pl-bulk-actions { display: flex; gap: 8px; }
        .pl-bulk-btn {
          display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px;
          border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer;
          border: 1px solid transparent; transition: all .14s; font-family: 'Inter', sans-serif;
        }
        .pl-bulk-btn svg { fill: none !important; }
        .pl-bulk-btn.danger {
          background: linear-gradient(135deg, #ef4444, #f43f5e);
          color: #fff; border: none;
          box-shadow: 0 3px 10px rgba(239,68,68,.28);
        }
        .pl-bulk-btn.danger:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(239,68,68,.4); }
        .pl-bulk-btn.danger:disabled { opacity: .6; cursor: not-allowed; }
        .pl-bulk-btn.ghost { background: #fff; color: var(--pl-muted); border-color: var(--pl-border); }
        .pl-bulk-btn.ghost:hover { background: #f6fbf8; }

        /* ── iOS Toggle Switch ── */
        .pl-toggle-wrap { display: flex; align-items: center; gap: 9px; }
        .pl-toggle-label { font-size: 12.5px; font-weight: 600; white-space: nowrap; }
        .pl-toggle-label.is-active  { color: #047857; }
        .pl-toggle-label.is-inactive { color: #9ca3af; }
        .pl-toggle-label.in-stock   { color: #1d4ed8; }
        .pl-toggle-label.out-stock  { color: #9ca3af; }

        .ios-switch {
          position: relative; display: inline-flex; align-items: center;
          width: 44px; height: 26px; border-radius: 999px;
          border: none; outline: none; cursor: pointer; padding: 0; flex-shrink: 0;
          transition: background .28s cubic-bezier(.25,.46,.45,.94);
          box-shadow: inset 0 0 0 1px rgba(0,0,0,.08), 0 2px 6px rgba(0,0,0,.1);
        }
        .ios-switch.is-on {
          background: var(--switch-on-bg, var(--pl-primary-grad));
          box-shadow: inset 0 0 0 1px rgba(0,0,0,.04), 0 3px 10px rgba(16,185,129,.35);
        }
        .ios-switch.is-off {
          background: #d1d5db;
          box-shadow: inset 0 0 0 1px rgba(0,0,0,.08), 0 2px 6px rgba(0,0,0,.08);
        }
        .ios-switch:disabled { opacity: .55; cursor: not-allowed; }
        .ios-switch__thumb {
          position: absolute; left: 3px;
          width: 20px; height: 20px; border-radius: 50%;
          background: #fff;
          box-shadow: 0 2px 6px rgba(0,0,0,.18), 0 0 0 0.5px rgba(0,0,0,.04);
          transition: transform .28s cubic-bezier(.25,.46,.45,.94), box-shadow .2s;
        }
        .ios-switch.is-on  .ios-switch__thumb { transform: translateX(18px); }
        .ios-switch.is-off .ios-switch__thumb { transform: translateX(0); }
        .ios-switch:not(:disabled):hover .ios-switch__thumb {
          box-shadow: 0 3px 10px rgba(0,0,0,.22), 0 0 0 0.5px rgba(0,0,0,.04);
        }

        /* ── Table Cell Styles ── */
        .pl-id-pill { display: inline-block; padding: 4px 9px; background: #eef4f1; color: #4b5b56; border-radius: 999px; font-size: 12px; font-weight: 700; letter-spacing: .02em; font-family: "SFMono-Regular", "Consolas", monospace; }
        .pl-img-cell img { width: 48px; height: 48px; border-radius: 10px; object-fit: cover; border: 1px solid var(--pl-border); background: #f6fbf8; display: block; }
        .pl-product-cell { display: flex; flex-direction: column; gap: 3px; padding: 4px 0; }
        .pl-product-name { font-weight: 700; color: var(--pl-text); line-height: 1.3; }
        .pl-product-brand { font-size: 12px; color: var(--pl-muted); font-weight: 500; }
        .pl-tag-pill { display: inline-block; padding: 4px 11px; background: #eef4f1; color: #3d4a45; border-radius: 999px; font-size: 12px; font-weight: 600; }
        .pl-tag-pill.secondary { background: #eff6ff; color: #1e40af; }
        .pl-price-cell { font-weight: 700; color: var(--pl-primary-d); font-size: 14px; }
        .pl-stock-qty { font-weight: 600; color: var(--pl-text); font-size: 13.5px; }
        .pl-date-cell { color: var(--pl-muted); font-size: 12.5px; }
        .pl-row-actions { display: inline-flex; gap: 6px; }
        .pl-ico-btn {
          width: 34px; height: 34px; border-radius: 9px;
          border: 1.5px solid transparent; background: transparent;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all .15s; text-decoration: none; flex-shrink: 0;
        }
        .pl-ico-btn svg { fill: none !important; }
        .pl-ico-btn.edit { color: #0d9488; border-color: #b7e8d2; background: #f0fbf6; }
        .pl-ico-btn.edit:hover { background: #d1fae5; border-color: #5eead4; color: #0f766e; transform: translateY(-1px); }
        .pl-ico-btn.del  { color: #dc2626; border-color: #fca5a5; background: #fff1f0; }
        .pl-ico-btn.del:hover  { background: #fee2e2; border-color: #ef4444; color: #b91c1c; transform: translateY(-1px); }

        /* ── Custom Checkboxes ── */
        .rdt_TableHead input[type="checkbox"],
        .rdt_TableBody input[type="checkbox"] {
          appearance: none; -webkit-appearance: none;
          width: 17px; height: 17px; border: 1.5px solid #c7d3ce; border-radius: 5px;
          background: #fff; cursor: pointer; position: relative; transition: all .14px; margin: 0;
        }
        .rdt_TableHead input[type="checkbox"]:hover,
        .rdt_TableBody input[type="checkbox"]:hover { border-color: var(--pl-primary); }
        .rdt_TableHead input[type="checkbox"]:checked,
        .rdt_TableBody input[type="checkbox"]:checked { background: var(--pl-primary); border-color: var(--pl-primary); }
        .rdt_TableHead input[type="checkbox"]:checked::after,
        .rdt_TableBody input[type="checkbox"]:checked::after {
          content: ""; position: absolute; left: 4px; top: 1px;
          width: 5px; height: 9px; border: solid #fff; border-width: 0 2px 2px 0; transform: rotate(45deg);
        }
        .rdt_TableHead input[type="checkbox"]:indeterminate { background: var(--pl-primary); border-color: var(--pl-primary); }
        .rdt_TableHead input[type="checkbox"]:indeterminate::after {
          content: ""; position: absolute; left: 3px; top: 6px; width: 9px; height: 2px; background: #fff; border-radius: 1px;
        }

        /* ── Custom Pagination ── */
        .pl-pagination {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap;
          padding: 14px 18px; border-top: 1px solid #eef4f1;
          font-size: 13px; color: var(--pl-muted);
        }
        .pl-pagination__info { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .pl-pagination__info b { color: var(--pl-text); }
        .pl-pagination__perpage {
          padding: 6px 10px; border-radius: 8px; border: 1px solid var(--pl-border);
          background: #f6fbf8; font-size: 13px; color: var(--pl-text);
          cursor: pointer; outline: none; transition: border-color .15s;
          font-family: 'Inter', sans-serif;
        }
        .pl-pagination__perpage:focus { border-color: var(--pl-primary); }
        .pl-pagination__pages { display: flex; align-items: center; gap: 4px; }
        .pl-pagination__btn {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 34px; height: 34px; padding: 0 6px;
          border-radius: 8px; border: 1px solid var(--pl-border);
          background: #fff; font-size: 13.5px; font-weight: 500;
          color: var(--pl-text); cursor: pointer; transition: all .15s ease;
          font-family: 'Inter', sans-serif;
        }
        .pl-pagination__btn svg { fill: none !important; }
        .pl-pagination__btn:hover:not(:disabled) {
          border-color: var(--pl-primary); color: var(--pl-primary); background: var(--pl-soft);
        }
        .pl-pagination__btn.is-active {
          background: var(--pl-primary-grad); color: #fff;
          border-color: transparent; box-shadow: 0 4px 12px rgba(16,185,129,.3); font-weight: 700;
        }
        .pl-pagination__btn:disabled { opacity: .4; cursor: not-allowed; }
        .pl-pagination__ellipsis {
          display: inline-flex; align-items: center; justify-content: center;
          width: 30px; color: var(--pl-muted);
        }

        /* ── Loading Overlay ── */
        .pl-overlay {
          position: fixed; inset: 0; z-index: 1080;
          background: rgba(243,250,246,.6); backdrop-filter: blur(4px);
          display: grid; place-items: center;
        }
        .pl-spinner {
          width: 42px; height: 42px; border-radius: 50%;
          border: 3px solid #d6efe2; border-top-color: var(--pl-primary);
          animation: plspin 0.8s linear infinite;
        }
        @keyframes plspin { to { transform: rotate(360deg); } }

        /* ══ IMAGE UPLOADER ══ */
        .pl-img-uploader { display: flex; flex-direction: column; gap: 12px; }
        .pl-img-dropzone {
          border: 2px dashed var(--pl-border); border-radius: 14px; padding: 28px 20px;
          text-align: center; cursor: pointer; background: #f6fbf8;
          transition: all .2s; color: var(--pl-muted);
        }
        .pl-img-dropzone:hover, .pl-img-dropzone.over {
          border-color: var(--pl-primary); background: #ecfdf5; color: var(--pl-primary-d);
        }
        .pl-img-dropzone svg { margin: 0 auto 10px; display: block; fill: none !important; }
        .pl-img-dropzone p { font-size: 14px; font-weight: 600; margin: 0 0 4px; }
        .pl-img-dropzone p span { color: var(--pl-primary); text-decoration: underline; }
        .pl-img-dropzone small { font-size: 12px; }
        .pl-img-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); gap: 10px; }
        .pl-img-thumb {
          position: relative; border-radius: 12px; overflow: hidden;
          aspect-ratio: 1; border: 1.5px solid var(--pl-border);
          cursor: grab; background: #f6fbf8; transition: box-shadow .2s;
        }
        .pl-img-thumb:hover { box-shadow: 0 4px 14px rgba(16,30,24,.14); }
        .pl-img-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .pl-img-primary-tag {
          position: absolute; top: 6px; left: 6px; z-index: 2;
          background: var(--pl-primary); color: #fff;
          font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 999px;
          letter-spacing: .03em;
        }
        .pl-img-overlay {
          position: absolute; inset: 0; background: rgba(15,31,26,.45);
          display: flex; align-items: flex-end; justify-content: space-between;
          padding: 6px; opacity: 0; transition: opacity .18s;
        }
        .pl-img-thumb:hover .pl-img-overlay { opacity: 1; }
        .pl-img-grip { color: #fff; display: grid; place-items: center; cursor: grab; }
        .pl-img-grip svg { fill: none !important; }
        .pl-img-remove {
          width: 24px; height: 24px; border-radius: 6px; border: none;
          background: var(--pl-del-bg); color: var(--pl-del-fg); cursor: pointer;
          display: grid; place-items: center; padding: 0; transition: background .14s;
        }
        .pl-img-remove svg { fill: none !important; }
        .pl-img-remove:hover { background: #fdd1cf; }
        .pl-img-hint { font-size: 12px; color: var(--pl-muted); display: flex; align-items: center; gap: 6px; margin: 0; }
        .pl-img-hint svg { fill: none !important; }

        /* ══ MODAL ══ */
        .pl-modal-overlay {
          position: fixed; inset: 0; z-index: 1090;
          background: rgba(10,25,20,.45); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center; padding: 20px;
          animation: plFadeIn .2s ease;
        }
        @keyframes plFadeIn { from { opacity:0; } to { opacity:1; } }
        .pl-modal-box {
          background: var(--pl-card); border-radius: 20px; width: 100%; max-width: 520px;
          box-shadow: var(--pl-shadow-lg); overflow: hidden;
          animation: plSlideUp .25s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes plSlideUp { from { opacity:0; transform: translateY(24px) scale(.97); } to { opacity:1; transform: translateY(0) scale(1); } }
        .pl-modal-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px 24px; background: linear-gradient(135deg, #ecfdf5, #e8f8f0);
          border-bottom: 1px solid var(--pl-border);
        }
        .pl-modal-title { display: flex; align-items: center; gap: 10px; font-weight: 800; font-size: 16px; color: var(--pl-text); font-family: 'inter', sans-serif; }
        .pl-modal-icon {
          width: 32px; height: 32px; border-radius: 9px; display: grid; place-items: center;
          color: #fff; flex-shrink: 0; box-shadow: 0 4px 10px rgba(16,185,129,.3);
        }
        .pl-modal-icon svg { fill: none !important; }
        .pl-modal-close {
          background: transparent; border: none; cursor: pointer; color: var(--pl-muted);
          border-radius: 8px; padding: 6px; line-height: 0; transition: all .14s;
        }
        .pl-modal-close svg { fill: none !important; }
        .pl-modal-close:hover { background: #f0f7f4; color: var(--pl-text); }
        .pl-modal-body { padding: 24px; }
        .pl-modal-foot {
          padding: 16px 24px; border-top: 1px solid var(--pl-border);
          background: #fafefb; display: flex; justify-content: flex-end; gap: 10px;
        }
        .pl-import-desc { font-size: 13.5px; color: var(--pl-muted); margin: 0 0 16px; line-height: 1.6; }
        .pl-import-desc code { background: #f0fbf6; color: var(--pl-primary-d); padding: 2px 6px; border-radius: 5px; font-size: 12px; }
        .pl-import-drop {
          border: 2px dashed var(--pl-border); border-radius: 14px; padding: 32px 20px;
          text-align: center; cursor: pointer; background: #f6fbf8;
          transition: all .2s; color: var(--pl-muted);
        }
        .pl-import-drop:hover, .pl-import-drop.has-file {
          border-color: #22c55e; background: #f0fdf4; color: #166534;
        }
        .pl-import-drop svg { margin: 0 auto 12px; display: block; fill: none !important; }
        .pl-import-drop p { font-size: 14px; font-weight: 600; margin: 0 0 4px; }
        .pl-import-drop small { font-size: 12px; }
        .pl-import-filename { font-size: 15px; font-weight: 700; margin: 0 0 4px; }
        .pl-template-link {
          display: inline-block; margin-top: 12px; font-size: 13px;
          color: var(--pl-primary-d); font-weight: 600; text-decoration: none;
        }
        .pl-template-link:hover { text-decoration: underline; }

        /* ── Confirm Dialog ── */
        .cdlg-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(15, 31, 26, 0.45); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px; animation: cdlgFadeIn .18s ease;
        }
        @keyframes cdlgFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .cdlg-box {
          background: #fff; border-radius: 20px;
          box-shadow: 0 24px 60px rgba(15,31,26,.2), 0 4px 16px rgba(15,31,26,.08);
          padding: 32px 28px 24px; width: 100%; max-width: 400px;
          text-align: center; animation: cdlgSlideUp .22s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes cdlgSlideUp {
          from { opacity: 0; transform: translateY(18px) scale(.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .cdlg-icon-wrap {
          width: 60px; height: 60px; border-radius: 18px;
          display: flex; align-items: center; justify-content: center; margin: 0 auto 18px;
        }
        .cdlg-icon-danger  { background: #fff1f0; color: #e0413c; }
        .cdlg-icon-warning { background: #fffbeb; color: #d97706; }
        .cdlg-title {
          font-family: 'inter', sans-serif; font-size: 18px; font-weight: 800;
          color: #0f1f1a; margin: 0 0 8px; letter-spacing: -0.3px;
        }
        .cdlg-message { font-size: 14px; color: #6b7a74; line-height: 1.6; margin: 0 0 24px; }
        .cdlg-actions { display: flex; gap: 10px; justify-content: center; }
        .cdlg-btn {
          flex: 1; padding: 11px 20px; border-radius: 10px; border: none;
          font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600;
          cursor: pointer; transition: all .15s;
        }
        .cdlg-btn-cancel { background: #f1f5f9; color: #475569; border: 1.5px solid #e2e8f0; }
        .cdlg-btn-cancel:hover { background: #e2e8f0; color: #1e293b; }
        .cdlg-btn-confirm { color: #fff; }
        .cdlg-btn-danger  { background: linear-gradient(135deg, #ef4444, #f43f5e); box-shadow: 0 4px 14px rgba(239,68,68,.28); }
        .cdlg-btn-danger:hover  { box-shadow: 0 6px 20px rgba(239,68,68,.4); transform: translateY(-1px); }
        .cdlg-btn-warning { background: linear-gradient(135deg, #f59e0b, #d97706); box-shadow: 0 4px 14px rgba(245,158,11,.28); }
        .cdlg-btn-warning:hover { box-shadow: 0 6px 20px rgba(245,158,11,.4); transform: translateY(-1px); }

        /* ── No Data ── */
        .pl-nodata { padding: 48px 20px; color: var(--pl-muted); text-align: center; }
        .pl-nodata svg { fill: none !important; margin: 0 auto 12px; display: block; }
        .pl-nodata p { font-weight: 600; margin: 0 0 4px; font-size: 15px; }
        .pl-nodata small { font-size: 13px; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .pl-head { flex-direction: column; align-items: stretch; }
          .pl-head-actions { width: 100%; }
          .pl-head-actions .pl-btn-primary,
          .pl-head-actions .pl-btn-secondary { flex: 1; justify-content: center; }
          .pl-card-top { flex-direction: column; align-items: stretch; }
          .pl-search-box { max-width: 100%; flex: none; }
          .pl-bulk-bar { flex-wrap: wrap; gap: 10px; padding: 10px 14px; }
          .pl-bulk-actions { justify-content: flex-end; }
          .pl-pagination { flex-direction: column; align-items: center; gap: 10px; }
          .pl-modal-box { border-radius: 16px; }
        }
        @media (max-width: 480px) {
          .pl-head-actions { flex-direction: column; }
        }

        /* DataTable overrides */
        .rdt_TableHead { font-family: 'inter', sans-serif; }
        .rdt_TableCol_Sortable { font-weight: 700 !important; }
        .rdt_Table svg { fill: none !important; }
      `}</style>

      <div id="layout-wrapper" className="pl-page">
        <Header />
        {(loading || bulkDeleting) && (
          <div className="pl-overlay"><div className="pl-spinner" /></div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">

              {/* ── Page Header ── */}
              <div className="pl-head">
                <div className="pl-head-left">
                  <h2>Products</h2>
                  <p>Manage your product catalogue — add, edit, and organize.</p>
                </div>
                <div className="pl-head-actions">
                  <button
                    type="button"
                    className="pl-btn-secondary"
                    onClick={() => setShowImport(true)}
                  >
                    <ExcelIcon size={16} />
                    Import Excel
                  </button>
                  <Link to="/product/add" className="pl-btn-primary">
                    <PlusIcon size={17} />
                    Add Product
                  </Link>
                </div>
              </div>

              {/* ── Main Card ── */}
              <div className="pl-card">

                {/* Top Bar */}
                <div className="pl-card-top">
                  <div className="pl-search-box">
                    <SearchIcon size={16} />
                    <input
                      type="text"
                      placeholder="Search by product name or brand…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>

                  {/* <MultiSelectFilter
                    options={statusOptions}
                    selected={statusFilter}
                    onChange={setStatusFilter}
                    placeholder="Status"
                  />

                  <MultiSelectFilter
                    options={stockOptions}
                    selected={stockFilter}
                    onChange={setStockFilter}
                    placeholder="Stock"
                  /> */}

                  <div className="pl-total-badge">
                    <TagIcon size={15} />
                    Total:&nbsp;<b>{totalRows}</b>
                  </div>
                </div>

                {/* Alert */}
                {messagealert && (
                  <div className={`pl-alert ${messageType}`} role="alert">
                    <InfoIcon size={16} />
                    {messagealert}
                    <button type="button" className="close" onClick={() => setMessageAlert("")}>×</button>
                  </div>
                )}

                {/* Bulk Bar */}
                {selectedRows.length > 0 && (
                  <div className="pl-bulk-bar">
                    <div className="pl-bulk-count">
                      <span className="pl-badge-num">{selectedRows.length}</span>
                      product{selectedRows.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="pl-bulk-actions">
                      <button
                        type="button"
                        className="pl-bulk-btn ghost"
                        onClick={() => { setSelectedRows([]); setToggleCleared((t) => !t); }}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="pl-bulk-btn danger"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleting}
                      >
                        <TrashIcon size={14} />
                        {bulkDeleting ? "Deleting…" : "Delete Selected"}
                      </button>
                    </div>
                  </div>
                )}

                {/* DataTable — built-in pagination hidden; custom below */}
                <DataTable
                  columns={columns}
                  data={filteredProducts}
                  customStyles={tableStyles}
                  selectableRows
                  onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
                  clearSelectedRows={toggleCleared}
                  highlightOnHover
                  responsive
                  striped
                  pagination={false}
                  noDataComponent={
                    <div className="pl-nodata">
                      <PackageIcon size={40} />
                      <p>No products found.</p>
                      <small>Try adjusting your search or filters.</small>
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {totalRows > 0 && (
                  <CustomPagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={handlePerPageChange}
                  />
                )}
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* ── Excel Import Modal ── */}
      {showImport && (
        <ExcelImportModal
          onClose={() => setShowImport(false)}
          onImport={() => fetchProducts(page, perPage)}
        />
      )}

      {/* ── Custom Confirm Dialog ── */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        confirmVariant={confirmDialog.confirmVariant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
    </>
  );
};

export default ProductList;